import { Component, OnInit, Input, inject } from '@angular/core';
import { Base } from '../../component/wrapper/base-wrapper.component';
import { filter, lastValueFrom, map, take, takeUntil } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { AUTHORIZED_ACCOUNT_LABEL, LocationPrivate, LocationPublic } from '../../../../globalTypes/account.types';
import { User } from '../../../../globalTypes/user.types';
import { AuthService } from '../../services/auth.service';
import { Store } from '@ngrx/store';
import { AccountActions, accountFeature } from '../../store/account.store';
import { authUserFeature } from '../../store/authUser.store';
import { GridTableComponent, GridTableOptions } from "../../component/layout/element/grid-table.component";
import { getLocationUsers, GetLocationUsersVariables, GetTeamMemberLocationsData } from '@dataconnect/account';
import { Logger } from '../../helper/logger';
import { AuthUser } from '../../../../globalTypes/auth.types';
import { UserData, UserDialog } from './user.dialog';
import { MatDialog } from '@angular/material/dialog';
import { VerifiedLocationService } from '../../services/verified.location.service';
import { InviteDialogComponent, InviteDialogData } from './invite.dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { makeGenericInviteMessage } from '../../../../globalTypes/configs';
import { ButtonComponent } from "../../component/layout/element/button.component";
import { AccountService } from '../../services/account.service';
import { ActivatedRoute } from '@angular/router';
const log = new Logger('ManageUsersComponent');

interface TeamMemberRow {
  uid: string | undefined;
  locationId: string | undefined;
  displayName: string | undefined;
  email: string | undefined;
  accounts: number | undefined;
  _actions: {label: string, icon: string, func: Function}[];     
}

@Component({
  selector: 'msh-manage-users',
  imports: [GridTableComponent, ButtonComponent],
  template: `
    <div class=" m-4 mt-9 max-w-5xl mx-auto flex justify-end">
      <msh-button (onClick)="inviteUserDialog()" cta="Invite User"></msh-button>
    </div>
    
    @if(invitesForYou() && invitesForYou().length > 0){
      <div class=" mb-4 m-4 max-w-5xl mx-auto">
        <div class="mb-2">Invitations for you</div>
      </div>
      <div class=" m-4 max-w-5xl mx-auto">
        <msh-grid-table
        [data]="invitesForYou() || []"
        [options]="tableOptions"
        [nullMsg]="'You have no outstanding invitations to view'"
        ></msh-grid-table>
      </div>
    }
    @if(users && showUsers){
      <div class=" m-4 max-w-5xl mx-auto">
        <div class=" text-lg font-bold mb-4">Users</div>
        <msh-grid-table
        [data]="users"
        [nullMsg]="'There are no users'"
        (onRecordClick)="onRecordClick($event)"
        ></msh-grid-table>
      </div>
    }
    @if(location && location.verified && teamMembers.length > 0){
      <div class="mt-8 m-4 max-w-5xl mx-auto">
        <div class=" text-lg font-bold mb-2">Other Account holders Using {{location.name}}</div>
        <msh-grid-table
        [data]="teamMembers"
        [options]="teamMemberOptions"
        [nullMsg]="'There are no team members'"
        (onRecordClick)="onRecordClick($event)"
        ></msh-grid-table>
      </div>
    }
    <div class="mt-8 mb-4 m-4 max-w-5xl mx-auto">
      <div class="mb-2  text-lg font-bold">Invitations sent from you</div>
    </div>
    @if(invitesFromYou()){
      <div class=" m-4 max-w-5xl mx-auto">
        <msh-grid-table
        [data]="invitesFromYou() || []"
        [options]="fromYouTableOptions"
        [nullMsg]="'You have no outstanding invitations to view'"
        ></msh-grid-table>
      </div>
    }
    @else {
      <div class=" null">You haven't sent any invitations that are pending</div>
    }
  `,
  styles: ``
})
export class ManageUsersComponent extends Base<ManageUsersComponent> implements OnInit{
  @Input() showUsers = true;
  location: LocationPublic | null = null;
  authUser?: AuthUser;
  userData!: UserData;
  teamMembers: TeamMemberRow[] = [];
  users: typeof this.userData.user[] = [];
  teamMemberOptions = {actionsKey: "_actions", excludeKeys: ["uid", "locationId"]}
  invitesForYou = toSignal(inject(Store).select(accountFeature.selectInvitationsReceived)
    .pipe(takeUntil(this.$destroy), 
      map(invites => invites.filter(invite => invite.isActive && !invite.respondedAt)
        .map(invite => ({...invite, _actions: [{label: 'Accept Invite', icon: 'check', func: () => this.accountService.acceptInvite(invite)}]})))), {initialValue: []})
  invitesFromYou = toSignal(inject(Store).select(accountFeature.selectInvitationsSent)
    .pipe(takeUntil(this.$destroy)))
  tableOptions: GridTableOptions = {
    formatTime: ["invitedToLocationId", "expiresAt"],//
    excludeKeys: ["updatedAt", "updatedById","invitedToLocationId", "createdAt","invitedById"],
    actionsKey: '_actions',
  }
  fromYouTableOptions: GridTableOptions = {
    formatTime: ["invitedToLocationId", "respondedAt", "expiresAt"],//
    excludeKeys: ["updatedAt", "updatedById","invitedToSegmentId", "invitedBy", 'id', "isActive", "invitedToLocationId", "createdAt","invitedById"],
    actionsKey: '_actions',
  }

  constructor(
    private authService: AuthService,
    private accountService: AccountService,
    private snackbar: MatSnackBar,
    private verifiedLocationService: VerifiedLocationService,
    private dialog: MatDialog,
    private store: Store,
    private route: ActivatedRoute,
  ) { super();
    this.route.data.pipe(takeUntil(this.$destroy)).subscribe((data:any)=>{
      if(data?.isInvitePage){
        this.showUsers = false;
      }
    })
    this.store.select(accountFeature.selectLocation).pipe(takeUntil(this.$destroy)).subscribe(async location => {
      const user = await lastValueFrom(this.store.select(authUserFeature.selectUser).pipe(filter(u=>!!u), take(1)))
      this.location = location;
      console.log({location})
      console.log({user});
      this.authUser = user;
      this.getUsers()
      if(this.location?.verified){
        const teamMembers: GetTeamMemberLocationsData['locations'] = await this.verifiedLocationService.getTeamMembers(this.location as LocationPrivate);
        const invitesFromYouEmails = (await lastValueFrom(this.store.select(accountFeature.selectInvitationsSent).pipe(take(1)))).map(invite=>invite.email);
        console.log({invitesFromYouEmails})
        this.teamMembers = teamMembers.filter(tm=>{ 
          return tm.owner?.id !== user?.id && !invitesFromYouEmails.includes(tm.owner?.email || '')
        }).map(tm=>{
          return {
            uid: tm.owner?.id,
            locationId: tm.id,
            displayName: tm.owner?.displayName,
            email: tm.owner?.email,
            accounts: tm.owner?.userLocations_on_user.length,
            _actions: [{label:'Invite', icon:'email', func: this.inviteUserDialog.bind(this)},{label:'Report', icon:'report', func: this.reportUser.bind(this)}]
          }
        });
        console.log({teamMembers});
      }
      await lastValueFrom(this.store.select(authUserFeature.selectUser).pipe(filter(u=>!!u), take(1)))
      this.authService.connectToInvitationsSentUntil(this.$destroy).pipe(takeUntil(this.$destroy)).subscribe(invites=>{
        console.log({invites})
        this.store.dispatch(AccountActions.setInvitationsSent({invitationsSent: invites}));
      })
    });
  }
  async ngOnInit(): Promise<void> {
  }
  
  async inviteUserDialog(row?: TeamMemberRow){
    const authUser = await lastValueFrom(this.store.select(authUserFeature.selectUser).pipe(filter(u=>!!u), take(1)));
    const data: InviteDialogData = {
      user: {
        email: row?.email || '',
        displayName: row?.displayName || 'someone',
      },
      location: {
        id: this.location?.id || '',
        segment: {
          id: this.location?.segmentId+'' || '',
        },
        name: this.location?.name || '',
        profileImage: this.location?.profileImage || undefined,
      },
      inviteToYourLocation: true,
      message: makeGenericInviteMessage('', authUser?.displayName || '', this.location?.name || '', !!this.location?.verified)
    }
    console.log({inviteData: data})
    
    const dialog = this.dialog.open(InviteDialogComponent, {data});
    const results = await lastValueFrom(dialog.afterClosed().pipe(take(1)));
    if(results){
      this.snackbar.open("Invite sent successfully", "Close", {duration: 3000});
    }
    console.log({results})
  }
  
  reportUser(data:any){
    console.log({report: data})
  }

  async getUsers(){
    if(!this.location || !this.location.id) return;
    try{
      const vars: GetLocationUsersVariables = {locationId: this.location.id}
      log.log({vars})
      const usersRef = await getLocationUsers(vars);
      const userLocs = usersRef.data.userLocations
      this.users = userLocs.map(u=>({
        id: u.user.id,
        email: u.user.email,
        displayName: u.user.displayName,
        mobile: u.user.mobile,
        profileImageId: u.user.profileImage?.id,
        statusId: u.user.statusId,
        // isEmailVerified: u.user.isEmailVerified,
        // notes: u.user,
        // lastLogin: u.user.updatedAt,
      }) as unknown as typeof this.userData.user);
    } catch(err) {
      console.error({err})
    }
  }

  async onRecordClick(id: string ){
    const user: (typeof this.userData.user) | undefined = this.users.find(u=>u.id === id);
    if(!user) return
    const data: UserData = {
      user: user
    }
    const dialog = this.dialog.open(UserDialog, {data});
    await lastValueFrom(dialog.afterClosed().pipe(take(1)));
    this.getUsers();
  }

}
